import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Coins, Crown, Zap, Check, X } from "lucide-react";
import { HeroSection } from "@/components/HeroSection";
import { CharacterCreation } from "@/components/CharacterCreation";
import { AuthModal } from "@/components/AuthModal";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MobileHeader } from "@/components/MobileHeader";
import { Favorites } from "@/pages/Favorites";
import { MyProfile } from "@/pages/MyProfile";
import { Settings } from "@/pages/Settings";
import { ChatPage } from "@/pages/ChatPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { Explore } from "@/pages/Explore";
import { Premium } from "@/pages/Premium";
import { PrivacySettings } from "@/pages/PrivacySettings";
import { SubscriptionSettings } from "@/pages/SubscriptionSettings";
import { NotificationSettings } from "@/pages/NotificationSettings";
import { ChatList } from "@/pages/ChatList";

import { HelpCenter } from "@/pages/HelpCenter";
import { ContactSupport } from "@/pages/ContactSupport";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCapacitorPlatform } from "@/hooks/use-capacitor";
import { useTranslation } from "@/hooks/useTranslation";
import { validateUserIdForDB } from "@/lib/uuid-utils";
import { CompactLanguageToggle } from "@/components/CompactLanguageToggle";
// User and Session types will be defined locally
import { TokenBalanceModal } from "@/components/TokenBalanceModal";
import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { OnboardingFlow } from "@/components/OnboardingFlow";
import { BenefitsPage } from "@/components/BenefitsPage";
import { apiClient } from "@/api/ApiClient";
import { useAuth, LOGOUT_REDIRECT_STATE } from "@/contexts/AuthContext";
import type { AuthSessionResponse } from "@/auth/authTypes";
import { isDebug } from "@/config/env";
import { v4 as uuidv4 } from 'uuid';

type AppState = 'hero' | 'benefits' | 'onboarding' | 'character-creation' | 'auth' | 'chat' | 'chat-list' | 'chat-page' | 'my-boyfriends' | 'my-profile' | 'settings' | 'profile-page' | 'explore' | 'premium' | 'privacy-settings' | 'subscription-settings' | 'notification-settings' | 'help-center' | 'contact-support' | 'onboarding-error';

type OnboardingResult = { gender: 'male' | 'female' | 'non-binary' | null; age_range: string | null; name: string | null; looking_for: string | null; fulfillment_level: string | null; communication_style: string | null; notifications_allowed: boolean | null };

interface Character {
  id?: string;
  name: string;
  avatar: string;
  personality: string;
  communicationStyle: string;
  style: string;
  ethnicity: string;
  age: number;
  hairstyle: string;
  bodyType: string;
  clothing: string;
  tattoo: string;
  voice: string;
  interests: string[];
}

const Index = () => {
  const { t, language, syncLanguageFromServer } = useTranslation();
  const { user, setUser, initializing, applySession, logout: authLogout, refreshUserProfile } = useAuth();
  const [appState, setAppState] = useState<AppState>('hero');
  const [previousAppState, setPreviousAppState] = useState<AppState>('hero');
  const [character, setCharacter] = useState<Character | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalCharacter, setAuthModalCharacter] = useState<Character | null>(null);
  const [selectedBoyfriendId, setSelectedBoyfriendId] = useState<string>('');
  const [profilePageSource, setProfilePageSource] = useState<'chat' | 'explore' | 'myBoyfriends'>('chat');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [userTokens, setUserTokens] = useState(0);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [bottomNavVisible, setBottomNavVisible] = useState(true);
  
  // Navigation state management for preserving user's position
  const [exploreState, setExploreState] = useState<{
    currentView: 'grid' | 'profile' | 'chat';
    selectedBoyfriendId?: string;
    fromExplore?: boolean;
  }>({
    currentView: 'grid'
  });

  // Chat state management for preserving user's position in chat flow
  const [chatState, setChatState] = useState<{
    currentView: 'list' | 'chat' | 'profile';
    selectedBoyfriendId?: string;
    character?: Character | null;
  }>({
    currentView: 'list'
  });

  // Fetch user tokens from database
  const fetchUserTokens = async (userId: string) => {
    const userValidation = validateUserIdForDB(userId);
    if (!userValidation.isValid) {
      console.error('Invalid user ID for token fetch:', userValidation.error);
      return 0;
    }
    
    try {
      const data = await apiClient.get(`/user/tokens/${userId}`);
      return data?.tokens || 0;
    } catch (error) {
      console.error('Error fetching user tokens:', error);
      return 0;
    }
  };

  // Fetch fresh user data from server
  const fetchUserData = async () => {
    try {
      const userData = await apiClient.get('/users/me');
      if (userData.preferredLanguage) {
        syncLanguageFromServer(userData.preferredLanguage);
      }
      return {
        ...userData,
        emailVerified: userData.emailVerified || false,
        notifications_allowed: userData.notificationsAllowed || false,
        isPremium: userData.premium || false,
      };
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  // Add tokens to user account
  const addTokensToUser = async (tokensToAdd: number) => {
    try {
      const response = await apiClient.addTokens(tokensToAdd);
      
      if (response.success) {
        setUserTokens(response.newTokenCount);
        return true;
      } else {
        console.error('Failed to add tokens:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Error adding tokens:', error);
      return false;
    }
  };

  // Calculate current user tokens based on premium status
  const getCurrentUserTokens = () => {
    return userTokens;
  };
  const [showTokenBalanceModal, setShowTokenBalanceModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<'monthly' | 'yearly'>('monthly');
  const { toast } = useToast();
  const isMobile = useIsMobile();
  // Native shells draw the scroll indicator inside the web view, where the
  // floating controls of the pages (profile gallery, chat header) end up
  // above it; without it the app scrolls like every other native app.
  const capacitorPlatform = useCapacitorPlatform();
  const isNative = capacitorPlatform === 'ios' || capacitorPlatform === 'android';
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const st = location.state as { openLogin?: boolean } | null;
    if (st?.openLogin !== LOGOUT_REDIRECT_STATE.openLogin) return;
    setShowAuthModal(true);
    setAuthModalCharacter(null);
    setAppState("hero");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (initializing || !user) return;
    if (appState === "hero") {
      setAppState("explore");
    }
  }, [initializing, user, appState]);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [appState]);

  // Refresh user data when premium subscription is updated
  useEffect(() => {
    const handler = async () => {
      if (user?.id) {
        await refreshUserProfile();
        const freshUserData = await fetchUserData();
        if (freshUserData?.currentTokens !== undefined) {
          setUserTokens(freshUserData.currentTokens);
        }
      }
    };
    window.addEventListener('premium-updated', handler as EventListener);
    return () => window.removeEventListener('premium-updated', handler as EventListener);
  }, [user?.id, fetchUserData, refreshUserProfile]);

  const handleGetStarted = () => {
    console.log('handleGetStarted called');
    setAppState('benefits');
  };

  const handleBenefitsNext = () => {
    setAppState('onboarding');
  };

  // Onboarding answers kept while the account creation can be retried.
  const [pendingOnboarding, setPendingOnboarding] = useState<OnboardingResult | null>(null);
  const [retryingOnboarding, setRetryingOnboarding] = useState(false);

  const handleOnboardingComplete = async (onboardingData: OnboardingResult) => {
    console.log('Onboarding completed with data:', onboardingData);
    setHasCompletedOnboarding(true);
    
    try {
      // Generate GUID for the user
      const userId = uuidv4();
      
      // Prepare API request data according to the curl example
      const apiUserData = {
        id: userId,
        email: null, // No email for local users
        name: onboardingData.name || 'User',
        notificationsAllowed: onboardingData.notifications_allowed || false,
        fulfillmentLevel: onboardingData.fulfillment_level || null,
        gender: onboardingData.gender || null,
        ageRange: onboardingData.age_range || null,
        lookingFor: onboardingData.looking_for ? [onboardingData.looking_for] : [],
        communicationStyle: onboardingData.communication_style ? [onboardingData.communication_style] : [],
        preferredLanguage: language,
      };

      // Call API to create user
      console.log('Creating user via API:', apiUserData);
      const createdUser = (await apiClient.post('/users', apiUserData)) as AuthSessionResponse;
      console.log('User created successfully:', createdUser);

      await applySession(createdUser);

      setUser((prev) => ({
        ...(prev ?? { id: userId }),
        id: userId,
        name: onboardingData.name || 'User',
        isPremium: false,
        gender: onboardingData.gender || undefined,
        ageRange: onboardingData.age_range || undefined,
        looking_for: onboardingData.looking_for || undefined,
        fulfillment_level: onboardingData.fulfillment_level || undefined,
        communication_style: onboardingData.communication_style || undefined,
        notifications_allowed: onboardingData.notifications_allowed || undefined,
      }));
      
      setPendingOnboarding(null);
      // Navigate to premium page after onboarding
      setAppState('premium');
      
    } catch (error) {
      console.error('Error creating user via API:', error);
      // Without a server account there is no session: every later call
      // would fail and end in the login form with nothing to log into.
      // Stay out of the app and offer a retry instead.
      setPendingOnboarding(onboardingData);
      setAppState('onboarding-error');
    }
  };

  const handleRetryOnboarding = async () => {
    if (!pendingOnboarding) return;
    setRetryingOnboarding(true);
    try {
      await handleOnboardingComplete(pendingOnboarding);
    } finally {
      setRetryingOnboarding(false);
    }
  };

  const handleCharacterComplete = (newCharacter: Character) => {
    setCharacter(newCharacter);
    
    if (user) {
      // User is already logged in, go to new chat interface (ChatPage)
      setAppState('chat-page');
    } else {
      // User needs to authenticate first
      setAuthModalCharacter(newCharacter);
      setShowAuthModal(true);
    }
  };

  const handleLogin = () => {
    if (appState === 'premium') {
      sessionStorage.setItem('redirectAfterAuth', 'premium');
    }
    setAuthModalCharacter(null);
    setShowAuthModal(true);
  };

  // confirmed: the caller already showed its own (localized) logout dialog.
  const handleLogout = async (opts?: { confirmed?: boolean }) => {
    let accountDeleted = false;
    try {
      accountDeleted = sessionStorage.getItem('logoutReason') === 'deleted';
    } catch {
      /* ignore */
    }

    if (!opts?.confirmed && !accountDeleted && user && !user.email) {
      const proceed = window.confirm(`${t('profile.logout_warning')}\n\n${t('profile.logout_confirm')}`);
      if (!proceed) {
        return;
      }
    }

    setUserTokens(0);
    setFavorites(new Set());
    setAppState('hero');
    setHasCompletedOnboarding(false);

    let title = t('profile.logged_out');
    let description = t('profile.logged_out_desc');
    try {
      const reason = sessionStorage.getItem('logoutReason');
      if (reason === 'deleted') {
        title = t('profile.account_deleted_title');
        description = t('profile.account_deleted_desc');
      }
      sessionStorage.removeItem('logoutReason');
    } catch {
      /* ignore */
    }

    // After a deletion there is nothing to log back into.
    await authLogout({ openLogin: !accountDeleted });
    toast({ title, description });
  };

  const handleAuthComplete = (userData: { id: string; email: string; name?: string }, isSignup?: boolean) => {
    console.log('handleAuthComplete called with:', userData);
    console.log('authModalCharacter:', authModalCharacter);
    console.log('isSignup:', isSignup);
    
    const userValidation = validateUserIdForDB(userData.id);
    if (!userValidation.isValid) {
      // Silent failure - authentication error
      return;
    }
    
    setShowAuthModal(false);
    
    if (isSignup) {
      // User just signed up - redirect to email verification page
      navigate(`/verify-email?email=${encodeURIComponent(userData.email)}`);
      return;
    }
    
    setUser({
      id: userData.id,
      email: userData.email,
      name: userData.name,
    });

    void fetchUserData();
    
    if (authModalCharacter) {
      // User completed character creation and authenticated - go to NEW chat interface
      console.log('Setting app state to chat-page (NEW ChatPage)');
      // Set the character with a temporary ID for new characters
      const characterWithId = {
        ...authModalCharacter,
        id: authModalCharacter.id || `new-${Date.now()}`
      };
      setCharacter(characterWithId);
      setAppState('chat-page');
    } else {
      // Check if user should be redirected to premium page after auth
      const redirectPath = sessionStorage.getItem('redirectAfterAuth');
      if (redirectPath === 'premium') {
        sessionStorage.removeItem('redirectAfterAuth');
        setAppState('premium');
      } else {
        // User just logged in without character creation - go to explore
        console.log('Setting app state to explore');
        setAppState('explore');
      }
    }
  };

  const handleBackToHero = () => {
    setAppState('hero');
    setCharacter(null);
  };

  const handleBackFromOnboardingToBenefits = () => {
    setAppState('benefits');
  };

  const handleCloseAuth = () => {
    setShowAuthModal(false);
  };

  const handleNavigateToMyBoyfriends = () => {
    setAppState('my-boyfriends');
  };

  const handleNavigateToProfile = async () => {
    setAppState('my-profile');
    // Načti fresh data uživatele ze serveru
    const freshUserData = await fetchUserData();
    console.log('Fresh user data from server:', freshUserData);
    if (freshUserData) {
      // Aktualizuj user state s fresh daty
      setUser(prev => prev ? { ...prev, ...freshUserData } : null);
      // Aktualizuj tokeny pokud jsou v response
      if (freshUserData.currentTokens !== undefined) {
        console.log('Updating userTokens to:', freshUserData.currentTokens);
        setUserTokens(freshUserData.currentTokens);
      }
    }
  };

  const handleCredentialsUpdated = async (email: string) => {
    setUser(prev => (prev ? { ...prev, email } : null));
    const freshUserData = await fetchUserData();
    if (freshUserData) {
      setUser(prev => (prev ? { ...prev, ...freshUserData } : null));
      if (freshUserData.currentTokens !== undefined) {
        setUserTokens(freshUserData.currentTokens);
      }
    }
  };

  const handleNavigateToSettings = () => {
    setAppState('settings');
  };

  const handleNavigateToChat = (boyfriendId?: string) => {
    console.log('handleNavigateToChat called - setting appState to chat-page', { boyfriendId });
    if (boyfriendId) {
      setSelectedBoyfriendId(boyfriendId);
      // Save chat state when navigating to a specific chat
      setChatState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId,
        character: character
      });
    } else {
      // Clear character when navigating to general chat (not from character creation)
      setCharacter(null);
      setSelectedBoyfriendId('');
      // Save list state when no specific boyfriend is selected
      setChatState({
        currentView: 'list'
      });
    }
    
    // If we're coming from a profile page via explore, update the explore state to reflect we're now in chat
    if (appState === 'profile-page' && profilePageSource === 'explore') {
      setExploreState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId || selectedBoyfriendId,
        fromExplore: true
      });
    }
    
    setAppState('chat-page');
  };

  const handleNavigateToChatList = () => {
    setSelectedBoyfriendId(null);
    setAppState('chat-list');
  };

  const handleNavigateToCharacterCreation = () => {
    setAppState('character-creation');
  };


  const handleSelectBoyfriend = (boyfriend: any) => {
    console.log('handleSelectBoyfriend called with:', boyfriend);
    setCharacter(boyfriend);
    setAppState('chat-page');
    console.log('App state set to chat-page (NEW ChatPage with directory)');
  };

  const handleBackToChat = () => {
    setAppState('chat');
  };

  const handleNavigateToProfilePage = (boyfriendId: string) => {
    console.log('handleNavigateToProfilePage called with ID:', boyfriendId);
    setSelectedBoyfriendId(boyfriendId);
    setProfilePageSource('chat');
    // Save chat profile state
    setChatState({
      currentView: 'profile',
      selectedBoyfriendId: boyfriendId,
      character: character
    });
    setAppState('profile-page');
  };

  const handleBackFromProfilePage = () => {
    if (profilePageSource === 'explore') {
      // Reset explore state to grid when coming back from profile
      setExploreState(prev => ({ ...prev, currentView: 'grid' }));
      setAppState('explore');
    } else if (profilePageSource === 'myBoyfriends') {
      setAppState('my-boyfriends');
    } else {
      setAppState('chat-page');
    }
  };

  // Settings navigation handlers
  const handleNavigateToPrivacySettings = () => setAppState('privacy-settings');
  const handleNavigateToSubscriptionSettings = () => setAppState('subscription-settings');
  const handleNavigateToNotificationSettings = () => setAppState('notification-settings');
  
  const handleNavigateToHelpCenter = () => setAppState('help-center');
  const handleNavigateToContactSupport = () => setAppState('contact-support');
  
  // New navigation handlers
  const handleNavigateToExplore = () => {
    // Always go to explore grid view when clicking the bottom nav explore button
    setExploreState({ currentView: 'grid' });
    setAppState('explore');
  };

  const handleNavigateToChatButton = () => {
    console.log('handleNavigateToChatButton called with chatState:', chatState);
    // Restore the previous chat state
    if (chatState.currentView === 'profile' && chatState.selectedBoyfriendId) {
      console.log('Restoring to profile view with boyfriend:', chatState.selectedBoyfriendId);
      setSelectedBoyfriendId(chatState.selectedBoyfriendId);
      setCharacter(chatState.character || null);
      setProfilePageSource('chat');
      setAppState('profile-page');
    } else if (chatState.currentView === 'chat' && chatState.selectedBoyfriendId) {
      console.log('Restoring to chat view with boyfriend:', chatState.selectedBoyfriendId);
      setSelectedBoyfriendId(chatState.selectedBoyfriendId);
      setCharacter(chatState.character || null);
      setAppState('chat-page');
    } else {
      console.log('No previous chat state, showing conversations list');
      // Default to conversations list view
      setSelectedBoyfriendId('');
      setCharacter(null);
      setAppState('chat-list');
    }
  };
  const handleNavigateToPremium = () => {
    setPreviousAppState(appState);
    setAppState('premium');
  };

  const handleSelectBoyfriendFromExplore = (boyfriendId: string) => {
    setSelectedBoyfriendId(boyfriendId);
    setProfilePageSource('explore');
    setExploreState({
      currentView: 'profile',
      selectedBoyfriendId: boyfriendId,
      fromExplore: true
    });
    setAppState('profile-page');
  };

  const handleNavigateToChatFromExplore = (boyfriendId?: string) => {
    if (boyfriendId) {
      setSelectedBoyfriendId(boyfriendId);
      setExploreState({
        currentView: 'chat',
        selectedBoyfriendId: boyfriendId,
        fromExplore: true
      });
    }
    setAppState('chat-page');
  };

  // Token handling functions
  const handleTokensClick = () => {
    // All users can access token balance modal now
    setShowTokenBalanceModal(true);
  };

  const handlePurchaseTokens = async (tokens: number, paymentMethod: string) => {
    // This callback is only called in DEBUG mode from TokenBalanceModal
    // In production, tokens are added via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handlePurchaseTokens called in production mode - this should not happen');
      return;
    }
    
    if (user?.id) {
      console.log('User ID for token purchase:', user.id);
      
      // Authenticated users: use API
      const userValidation = validateUserIdForDB(user.id);
      if (!userValidation.isValid) {
        toast({
          title: t('tokens.error_title'),
          description: t('tokens.error_debug_purchase'),
          variant: "destructive",
        });
        return;
      }
      
      const success = await addTokensToUser(tokens);
      
      if (success) {
        // Token count is already updated in addTokensToUser
        setShowTokenBalanceModal(false);
        toast({
          title: t('tokens.debug_purchase_title'),
          description: t('tokens.debug_purchase_description', { count: tokens }),
        });
        // Minimal refresh: tokens state is already updated; UI will re-render without full reload
      } else {
        toast({
          title: t('tokens.error_title'),
          description: t('tokens.error_debug_purchase'),
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: t('tokens.error_title'),
        description: t('tokens.error_sign_in_purchase'),
        variant: "destructive",
      });
    }
  };


  // Check if boyfriend is favorited
  const checkFavoriteStatus = async (boyfriendId: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const status = await apiClient.get(`/favorites/${boyfriendId}/status`);
      return status?.isFavorited || false;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  };

  // Handle favorites globally
  const handleToggleFavorite = async (boyfriendId: string) => {
    console.log('Toggling favorite for boyfriend ID:', boyfriendId);
    console.log('Current favorites before toggle:', Array.from(favorites));
    console.log('User:', user);
    
    try {
      // Check if user is authenticated
      if (!user) {
        // For unauthenticated users, just update local state
        const isFavorited = favorites.has(boyfriendId);
        console.log('Unauthenticated user, updating local favorites only');
        
        if (isFavorited) {
          setFavorites(prev => {
            const newSet = new Set(prev);
            newSet.delete(boyfriendId);
            console.log('Removed from local favorites:', Array.from(newSet));
            return newSet;
          });
        } else {
          setFavorites(prev => {
            const newSet = new Set([...prev, boyfriendId]);
            console.log('Added to local favorites:', Array.from(newSet));
            return newSet;
          });
        }
        return;
      }

      // For authenticated users, use API
      const isFavorited = favorites.has(boyfriendId);
      console.log('Is currently favorited:', isFavorited, 'using ID:', boyfriendId);
      
      if (isFavorited) {
        // Remove from favorites
        console.log('Removing from favorites via API...');
        await apiClient.delete(`/favorites/${boyfriendId}`);
        
        console.log('Successfully removed from API, updating local state...');
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(boyfriendId);
          console.log('New favorites set:', Array.from(newSet));
          return newSet;
        });
        
      } else {
        // Add to favorites
        console.log('Adding to favorites via API...');
        await apiClient.put(`/favorites/${boyfriendId}`);
        
        console.log('Successfully added to API, updating local state...');
        setFavorites(prev => {
          const newSet = new Set([...prev, boyfriendId]);
          console.log('New favorites set:', Array.from(newSet));
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handlePremiumUpgrade = async () => {
    if (user) {
      // In a real app, this would integrate with payment processor
      setUser(prev => prev ? { ...prev, isPremium: true } : null);
      
      setShowPremiumModal(false);
    }
  };

  const handleUpgradeToPremium = async (plan?: 'monthly' | 'yearly') => {
    // This is only used in DEBUG mode
    // In production, premium status is set via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handleUpgradeToPremium called in production mode - premium should be set via webhook');
      return;
    }
    
    if (user) {
      // Upgrade the user to premium
      setUser(prev => prev ? { ...prev, isPremium: true } : null);
      
      // Silent upgrade - no notification
    }
  };

  const handleShowUpgradeModal = (plan: 'monthly' | 'yearly' = 'monthly') => {
    setUpgradePlan(plan);
    setShowUpgradeModal(true);
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    // This callback is only called in DEBUG mode from SubscriptionPaymentModal
    // In production, premium status is set via Stripe webhook after successful payment
    if (!isDebug) {
      console.warn('handlePurchaseSubscription called in production mode - this should not happen');
      return;
    }
    
    handleUpgradeToPremium(plan);
  };

  const handleCancelSubscription = async () => {
    if (user) {
      // Cancel the user's premium subscription
      setUser(prev => prev ? { ...prev, isPremium: false } : null);
      
      // Silent cancellation - no notification
    }
  };

  // Helper to determine which pages should show navigation
  const pageAllowsNav = !['hero', 'benefits', 'onboarding', 'onboarding-error', 'character-creation', 'premium'].includes(appState) && !showPremiumModal;
  const shouldShowNavigation = pageAllowsNav && (appState === 'chat-page' ? bottomNavVisible : true);

  // Handler for ChatPage to control bottom nav visibility
  const handleBottomNavVisibilityChange = (visible: boolean) => {
    setBottomNavVisible(visible);
  };

  // Helper to get current page for navigation
  const getCurrentPage = (): 'explore' | 'chat' | 'myBoyfriends' | 'myProfile' => {
    switch (appState) {
      case 'chat':
      case 'chat-list':
      case 'chat-page':
        return 'chat';
      case 'profile-page':
        // Return the source page for the profile page to highlight the correct nav button
        if (profilePageSource === 'chat') return 'chat';
        if (profilePageSource === 'myBoyfriends') return 'myBoyfriends';
        if (profilePageSource === 'explore') return 'explore';
        return 'explore';
      case 'my-boyfriends':
        return 'myBoyfriends';
      case 'my-profile':
      case 'contact-support':
      case 'help-center':
      case 'notification-settings':
      case 'privacy-settings':
      case 'subscription-settings':
        return 'myProfile';
      case 'explore':
        return 'explore';
      default:
        return 'explore';
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full">
      
      <main ref={mainRef} className={`flex-1 overflow-auto${isNative ? ' scrollbar-hide' : ''}`}>
            {appState === 'hero' && (
              <>
                <div className="fixed right-4 z-50" style={{ top: `calc(1rem + var(--safe-area-inset-top, 0px))` }}>
                  <CompactLanguageToggle />
                </div>
                <HeroSection onGetStarted={handleGetStarted} onLogin={handleLogin} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onSelectBoyfriend={handleSelectBoyfriend} />
              </>
            )}
            
            {appState === 'benefits' && (
              <BenefitsPage onNext={handleBenefitsNext} onBack={handleBackToHero} />
            )}
            
            {appState === 'onboarding' && (
              <OnboardingFlow onComplete={handleOnboardingComplete} onBack={handleBackFromOnboardingToBenefits} />
            )}

            {appState === 'onboarding-error' && (
              <div className="min-h-screen flex items-center justify-center bg-gradient-dreamy p-6">
                <div className="max-w-sm w-full text-center space-y-4">
                  <h2 className="text-2xl font-bold">{t('onboarding.create_failed_title')}</h2>
                  <p className="text-muted-foreground">{t('onboarding.create_failed_desc')}</p>
                  <Button variant="romantic" className="w-full" disabled={retryingOnboarding} onClick={() => void handleRetryOnboarding()}>
                    {retryingOnboarding ? t('common.loading') : t('onboarding.try_again')}
                  </Button>
                  <Button variant="ghost" className="w-full" disabled={retryingOnboarding} onClick={handleBackToHero}>
                    {t('onboarding.back')}
                  </Button>
                </div>
              </div>
            )}
      
      {appState === 'character-creation' && (
        <CharacterCreation 
          onComplete={handleCharacterComplete}
          onBack={user ? undefined : handleBackToHero}
          user={user}
          onNavigateToMyBoyfriends={user ? handleNavigateToMyBoyfriends : undefined}
          onNavigateToProfile={user ? handleNavigateToProfile : undefined}
          onNavigateToSettings={user ? handleNavigateToSettings : undefined}
          onNavigateToCharacterCreation={user ? handleNavigateToCharacterCreation : undefined}
          onNavigateToChat={handleNavigateToChat}
          onLogout={user ? handleLogout : undefined}
          userTokens={userTokens}
        />
      )}
      
      {appState === 'chat-page' && (
         <ChatPage 
          user={user}
          onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
          onNavigateToProfile={handleNavigateToProfile}
          onNavigateToSettings={handleNavigateToSettings}
          onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
          onNavigateToExplore={handleNavigateToExplore}
          onNavigateToPremium={handleNavigateToPremium}
          onLogout={handleLogout}
          onPromptLogin={handleLogin}
          onNavigateToProfilePage={handleNavigateToProfilePage}
          newCharacter={appState === 'chat-page' && character && character.id?.startsWith('new-') ? character : null}
          selectedBoyfriendId={selectedBoyfriendId}
          onTokensClick={handleTokensClick}
          userTokens={userTokens}
          onPurchaseTokens={handlePurchaseTokens}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
          onBottomNavVisibilityChange={handleBottomNavVisibilityChange}
          onBackToList={handleNavigateToChatList}
        />
      )}

      {appState === 'chat-list' && (
        <ChatList 
          user={user}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
          onNavigateToChat={handleNavigateToChat}
          onNavigateToPremium={handleNavigateToPremium}
          onNavigateToExplore={handleNavigateToExplore}
          onPromptLogin={handleLogin}
          onBottomNavVisibilityChange={handleBottomNavVisibilityChange}
        />
      )}
      
      {appState === 'profile-page' && (
        <ProfilePage 
          boyfriendId={selectedBoyfriendId}
          user={user}
          onBack={handleBackFromProfilePage}
          onPromptLogin={handleLogin}
          onNavigateToChat={() => setAppState('chat-page')}
          onNavigateToMyBoyfriends={() => setAppState('my-boyfriends')}
          onNavigateToProfile={() => setAppState('my-profile')}
          onNavigateToSettings={() => setAppState('settings')}
          onNavigateToCharacterCreation={() => setAppState('character-creation')}
          onNavigateToExplore={() => setAppState('explore')}
          onNavigateToPremium={handleNavigateToPremium}
          userTokens={userTokens}
          onTokensClick={handleTokensClick}
          onUpdateTokens={async (newTokens) => {
            if (!user) {
              toast({
                title: "Authentication Required",
                description: "Please sign in to continue",
                variant: "destructive",
              });
              return;
            }
            
            // If newTokens is the same as current, just update local state (no HTTP call)
            if (newTokens === userTokens) {
              return;
            }
            
            const tokensToAdd = newTokens - userTokens;
            
            // If tokens were deducted (negative diff), backend already did it - just update local state
            // Only make HTTP call for positive changes (purchases, additions)
            if (tokensToAdd > 0) {
              const success = await addTokensToUser(tokensToAdd);
              if (success) {
                toast({
                  title: t('tokens.toast_updated_title'),
                  description: t('tokens.toast_updated_description'),
                });
                try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
              } else {
                toast({
                  title: t('tokens.error_title'),
                  description: t('tokens.toast_update_failed'),
                  variant: "destructive",
                });
              }
            } else {
              // Negative change - backend already handled it, just update local state
              setUserTokens(newTokens);
              try { window.dispatchEvent(new CustomEvent('tokens-updated')); } catch {}
            }
          }}
          onPurchaseTokens={handlePurchaseTokens}
          source={profilePageSource}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
        />
      )}
      
      {appState === 'my-boyfriends' && (
        <Favorites 
          onSelectBoyfriend={handleSelectBoyfriend}
          onNavigateToChat={handleNavigateToChat}
          onNavigateToProfile={handleNavigateToProfile}
          onNavigateToSettings={handleNavigateToSettings}
          onCreateNew={handleNavigateToCharacterCreation}
          onNavigateToExplore={handleNavigateToExplore}
          onNavigateToPremium={handleNavigateToPremium}
          onNavigateToBoyfriendProfile={(boyfriendId) => {
            console.log('MyBoyfriends onNavigateToBoyfriendProfile called with ID:', boyfriendId);
            setSelectedBoyfriendId(boyfriendId);
            setProfilePageSource('myBoyfriends');
            setAppState('profile-page');
          }}
          onToggleFavorite={handleToggleFavorite}
          user={user}
          onPromptLogin={handleLogin}
          onTokensClick={handleTokensClick}
          userTokens={userTokens}
          // No local favorites passed
        />
      )}
      
      {appState === 'my-profile' && (
        user ? (
          <MyProfile 
            onNavigateToChat={handleNavigateToChat}
            onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
            onNavigateToSettings={handleNavigateToSettings}
            onCreateNew={handleNavigateToCharacterCreation}
            onNavigateToExplore={handleNavigateToExplore}
            onNavigateToPremium={handleNavigateToPremium}
            onNavigateToHelpCenter={handleNavigateToHelpCenter}
            onNavigateToContactSupport={handleNavigateToContactSupport}
            onLogout={handleLogout}
            user={user}
            onTokensClick={handleTokensClick}
            userTokens={userTokens}
            onPurchaseTokens={handlePurchaseTokens}
            onCredentialsUpdated={handleCredentialsUpdated}
            onPurchaseSubscription={handlePurchaseSubscription}
          />
        ) : (
          // Redirect to login if not authenticated
          (() => {
            setShowAuthModal(true);
            setAppState('hero');
            return null;
          })()
        )
      )}
      
      {appState === 'settings' && (
        user ? (
           <Settings 
             onNavigateToChat={handleNavigateToChat}
             onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
             onNavigateToProfile={handleNavigateToProfile}
             onCreateNew={handleNavigateToCharacterCreation}
             onNavigateToPrivacySettings={handleNavigateToPrivacySettings}
             onNavigateToSubscriptionSettings={handleNavigateToSubscriptionSettings}
             onNavigateToNotificationSettings={handleNavigateToNotificationSettings}
             onNavigateToExplore={handleNavigateToExplore}
               onNavigateToPremium={handleNavigateToPremium}
                onTokensClick={handleTokensClick}
                onPurchaseTokens={handlePurchaseTokens}
                onUpgradeToPremium={handleUpgradeToPremium}
                user={{
                  email: user.email,
                  name: user.name,
                  isPremium: user.isPremium || false,
                  subscriptionTier: user.isPremium ? "Premium" : "Free",
                  subscriptionEnd: user.isPremium ? "2024-12-31T23:59:59Z" : undefined
                }}
                userTokens={userTokens}
            />
        ) : (
          // Redirect to login if not authenticated
          (() => {
            setShowAuthModal(true);
            setAppState('hero');
            return null;
          })()
        )
       )}
       
       {/* Settings Sub-pages */}
       {appState === 'privacy-settings' && (
         <PrivacySettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
       )}
       
        {appState === 'subscription-settings' && (
          <SubscriptionSettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onNavigateToExplore={handleNavigateToExplore} onCreateNew={handleNavigateToCharacterCreation} onCancelSubscription={handleCancelSubscription} user={{...user, isPremium: user?.isPremium || false, subscriptionTier: user?.isPremium ? "Premium" : "Free", subscriptionEnd: user?.isPremium ? "2024-12-31T23:59:59Z" : undefined}} />
        )}
       
       {appState === 'notification-settings' && (
         <NotificationSettings onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
       )}
       
       {appState === 'help-center' && (
         <HelpCenter onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
       )}
       
        
          {appState === 'explore' && (
            <>
              {exploreState.currentView === 'chat' && exploreState.selectedBoyfriendId ? (
                <ChatPage 
                  user={user}
                  onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
                  onNavigateToProfile={handleNavigateToProfile}
                  onNavigateToSettings={handleNavigateToSettings}
                  onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
                  onNavigateToExplore={() => {
                    setExploreState({ currentView: 'grid' });
                    setAppState('explore');
                  }}
                  onNavigateToPremium={handleNavigateToPremium}
                  onLogout={handleLogout}
                  onPromptLogin={handleLogin}
                  onNavigateToProfilePage={handleNavigateToProfilePage}
                  newCharacter={null}
                  selectedBoyfriendId={exploreState.selectedBoyfriendId}
                  onTokensClick={handleTokensClick}
                  userTokens={userTokens}
                  onPurchaseTokens={handlePurchaseTokens}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                  onBackToList={() => {
                    setExploreState({ currentView: 'grid' });
                    setAppState('explore');
                  }}
                />
              ) : (
                <Explore 
                  onNavigateToChat={handleNavigateToChatFromExplore}
                  onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
                  onNavigateToProfile={handleNavigateToProfile}
                  onNavigateToSettings={handleNavigateToSettings}
                  onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
                  onNavigateToExplore={handleNavigateToExplore}
                  onNavigateToPremium={handleNavigateToPremium}
                  onSelectBoyfriend={handleSelectBoyfriendFromExplore}
                  user={user}
                  onPromptLogin={handleLogin}
                  onTokensClick={handleTokensClick}
                  userTokens={userTokens}
                />
              )}
            </>
          )}
        
         {appState === 'premium' && (
           <Premium 
             onNavigateToChat={handleNavigateToChat}
             onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
             onNavigateToProfile={handleNavigateToProfile}
             onNavigateToSettings={handleNavigateToSettings}
             onNavigateToCharacterCreation={handleNavigateToCharacterCreation}
             onNavigateToExplore={handleNavigateToExplore}
             onNavigateToPremium={handleNavigateToPremium}
             user={user}
             onPromptLogin={handleLogin}
             onTokensClick={handleTokensClick}
             onUpgradeToPremium={() => handleUpgradeToPremium()}
             userTokens={userTokens}
             source={previousAppState === 'onboarding' ? 'onboarding' : previousAppState === 'chat' ? 'chat' : 'upgrade'}
             onGoBack={() => setAppState(previousAppState)}
           />
         )}

            
            {appState === 'contact-support' && (
             <ContactSupport onBack={handleNavigateToSettings} onNavigateToChat={handleNavigateToChat} onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends} onNavigateToProfile={handleNavigateToProfile} onNavigateToSettings={handleNavigateToSettings} onCreateNew={handleNavigateToCharacterCreation} user={user} />
           )}
      </main>

      {/* Bottom Navigation */}
      {shouldShowNavigation && (
        <BottomNavigation
          currentPage={getCurrentPage()}
          onNavigateToExplore={handleNavigateToExplore}
          onNavigateToChat={handleNavigateToChat}
          onNavigateToMyBoyfriends={handleNavigateToMyBoyfriends}
          onNavigateToProfile={handleNavigateToProfile}
          user={user}
          onPromptLogin={handleLogin}
          unreadMessagesCount={3}
        />
      )}
      
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuth}
        onComplete={handleAuthComplete}
        character={authModalCharacter || undefined}
      />
      
      <TokenBalanceModal
        open={showTokenBalanceModal}
        onOpenChange={setShowTokenBalanceModal}
        userTokens={getCurrentUserTokens()}
        onPurchaseTokens={handlePurchaseTokens}
      />

      {/* Premium Modal */}
      <Dialog open={showPremiumModal} onOpenChange={(open) => {
        setShowPremiumModal(open);
        // If closing the modal after onboarding, navigate to explore
        if (!open && appState === 'onboarding') {
          setAppState('explore');
        }
      }}>
        <DialogContent className="w-full h-full max-w-full max-h-full overflow-y-auto relative">
          <DialogHeader>
            <DialogTitle className="text-center text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              {t('premium_modal.upgrade_romance')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {/* Urgency Banner */}
            <div className="border-2 border-orange-500 bg-transparent text-orange-500 p-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">⏰</span>
                <span className="font-bold text-sm">{t('premium_modal.few_hours_left')}</span>
              </div>
            </div>

            {/* Features Section */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center mb-4">
                <Zap className="h-8 w-8 mx-auto mb-2" />
                <h2 className="text-lg font-bold mb-2">{t('premium_modal.premium_features')}</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unblurred_photos')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.hd_images')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.priority_responses')}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.free_tokens')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.generate_spicy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unlock_premium_bfs')}</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Pricing Plans */}
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 ${
                  selectedPlan === 'monthly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.monthly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$19.99</p>
                  <p className="text-2xl font-bold text-primary">$9.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_month')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.cancel_anytime')}</li>
                  <li>{t('premium_modal.free_trial')}</li>
                </ul>
                {selectedPlan === 'monthly' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>

              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${
                  selectedPlan === 'yearly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('yearly')}
              >
                <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
                    {t('premium_modal.best_value')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.yearly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$159.99</p>
                  <p className="text-2xl font-bold text-primary">$79.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_year')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50_plus')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.two_months_free')}</li>
                  <li>{t('premium_modal.priority_support')}</li>
                </ul>
                {selectedPlan === 'yearly' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => handleShowUpgradeModal(selectedPlan)}
                variant="romantic" 
                size="lg" 
                className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('premium_modal.upgrade_to_plan', { plan: selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan') })}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t('premium_modal.secure_payment')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Payment Modal */}
      <SubscriptionPaymentModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        selectedPlan={upgradePlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />
    </div>
  );
};

export default Index;
